import useTranscripts from 'api/hooks/useTranscripts';
import preventLink from 'components/Dashboard/TagsList/prevenLink';
import normalize from 'components/Dashboard/utilities/normalizeString';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import { useRef, useState, useContext, useLayoutEffect } from 'react';
import time from 'utils/time';
import { store } from 'components/Store';

const errorTooltipHtml = ({ errorTitle, errorExplanation, errorNextStep }) => {
  const rows = [
    errorTitle && `<p class="mb-2 specific-x-padding-24 title-h5-m-bold border-bottom py-3 z-max">${errorTitle}</p>`,
    errorExplanation && `<p class="pt-1 specific-x-padding-24 mb-0 title-h5 pb-2">${errorExplanation}</p>`,
    errorNextStep && `<p class="pb-3 mb-2 title-h5 specific-x-padding-24">${errorNextStep}</p>`,
  ].filter(Boolean);
  return rows.join('');
};

const TranscriptsItem = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { team, userProfile } = state;

  const userId = useRef(team?.allMembers?.find((item) => item.emailAddress === userProfile?.email)?.id);

  const { item, setActiveDebate, setIsOpen, setDeleteItem, setShowDeleteModal } = props;
  const {
    eventTitle,
    createdAtUtc,
    requestedByUserName,
    status,
    documentAvailable,
    notifyEmails,
    requestedByUserId,
    errorTitle,
    errorExplanation,
    errorNextStep,
  } = item;
  const inProgress = status === 'Processing';
  const emailed = status === 'Emailed';
  const finished = emailed || status === 'NoEmailed' || status === 'Error';
  const pending = status === 'Pending';
  const ready = emailed || status === 'NotEmailed';

  const allowUserActions = useRef(userId.current === requestedByUserId);
  const openNumberOfRecipients = useRef(
    allowUserActions.current && (!finished || (finished && !!notifyEmails?.length))
  );
  return (
    <li className={`border rounded content-item-link my-2 ${inProgress ? 'content-item-link-pending ' : ''}`}>
      <div className={`p-3`}>
        <div className='d-flex report-main-list-item'>
          <span className='report-list-icon text-center'>
            <span className='icon-document text-main-blue' />
          </span>
          <div className='flex-grow-1'>
            <div className='flex-centered justify-content-between'>
              <div className='flex-grow-1'>
                <h4 className='content-item-title mb-0'>{eventTitle}</h4>
                <p className='text-grey-description mt-1 mb-0 paragraph-p3'>
                  Requested by: {requestedByUserName}{' '}
                  <span className='heading-date'>
                    {' '}
                    <span className='dot' />
                  </span>{' '}
                  Requested on {time(createdAtUtc).formatGeneralDate()}
                </p>
              </div>

              {inProgress && (
                <span className='specific-x-padding-12 specific-y-padding-3 rounded paragraph-p3 bg-yellow-20  d-inline-block'>
                  In progress
                  <span className='client-loading-text position-relative'>
                    <span />
                    <span />
                    <span />
                  </span>
                </span>
              )}
              {pending && (
                <span className='specific-x-padding-12 specific-y-padding-3 rounded paragraph-p3 bg-main-grey-30 d-inline-block'>
                  Scheduled
                </span>
              )}
              {ready && (
                <span className='flex-centered'>
                  <span className='specific-x-padding-12 specific-y-padding-3 rounded paragraph-p3 bg-green-20 d-inline-block '>
                    Ready
                  </span>
                  {!!documentAvailable && <DownloadTranscriberButton item={item} />}
                </span>
              )}
              {!ready && !pending && !inProgress && (
                <span className='specific-x-padding-12 specific-y-padding-3 rounded paragraph-p3 bg-red-10 d-inline-block'>
                  {status}{' '}
                  {(!!errorTitle || !!errorExplanation || !!errorNextStep) && (
                    <span
                      className='icon-info paragraph-p6 ml-1 d-inline-block-centered centered-1'
                      data-tooltip-id={'clickeable-tooltip'}
                      data-tooltip-class-name={'contributions-tooltip sentiment-dot-tooltip z-extra-max'}
                      data-tooltip-html={errorTooltipHtml({ errorTitle, errorExplanation, errorNextStep })}
                      data-tooltip-place='top'
                    />
                  )}
                </span>
              )}

              <button
                className={`general-button border specific-x-padding-12 specific-y-padding-3 paragraph-p3 light-75-text rounded ml-2 ${openNumberOfRecipients.current ? 'hovered-button' : 'cursor-auto'}`}
                style={{ minWidth: '99px' }}
                onClick={() => {
                  if (openNumberOfRecipients.current) {
                    setActiveDebate({ ...item, inProgress: inProgress, existingTranscript: true });
                    setIsOpen(true);
                  }
                }}
              >
                {notifyEmails?.length ?? 0} recipient{notifyEmails?.length !== 1 ? 's' : ''}
              </button>
              {allowUserActions.current && (
                <TranscriptItemActionsButton
                  item={item}
                  inProgress={inProgress}
                  setActiveDebate={setActiveDebate}
                  setIsOpen={setIsOpen}
                  setDeleteItem={setDeleteItem}
                  setShowDeleteModal={setShowDeleteModal}
                  finished={finished}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

const TranscriptItemActionsButton = (props) => {
  const { item, setActiveDebate, setIsOpen, setDeleteItem, setShowDeleteModal, finished } = props;
  const { documentAvailable } = item ?? {};
  const [showActions, setShowActions] = useState(false);
  const [position, setPosition] = useState({ top: '0px', right: '0px', opacity: 0 });
  const actionsContainer = useRef(null);
  const buttonElement = useRef(null);
  const boxElement = useRef(null);

  const hideItem = () => {
    setShowActions(false);
  };

  useOutsideElement(actionsContainer, hideItem);

  useLayoutEffect(() => {
    if (showActions && !!buttonElement?.current) {
      const { top, bottom, right } = buttonElement.current.getBoundingClientRect();
      const boxHeight = boxElement?.current?.getBoundingClientRect()?.height ?? 0;
      const openToTop = bottom + boxHeight > window.innerHeight;
      setPosition({
        top: `${openToTop ? top - boxHeight - 3 : bottom + 3}px`,
        right: `${window.innerWidth - right}px`,
        opacity: 1,
      });
    }
  }, [showActions]);

  return (
    <>
      <div className='position-relative ml-1' ref={actionsContainer}>
        <button
          className={`general-button circle-button hover-background-gray-05 d-inline-block-centered specific-top-margin-2`}
          onClick={(e) => {
            e.stopPropagation();
            setShowActions(!showActions);
          }}
          ref={buttonElement}
        >
          <span className='icon-three-dots light-40-text' />
        </button>
        {showActions && (
          <div
            ref={boxElement}
            style={{ ...position, minWidth: 'max-content' }}
            className='box-shadow rounded py-2 position-fixed z-extra-max'
          >
            {!finished && (
              <button
                className={
                  'list-action-button w-100 general-button text-left specific-y-padding-5 specific-x-padding-24 flex-centered gap-10'
                }
                onClick={() => {
                  setActiveDebate({ ...item, existingTranscript: true });
                  setShowActions(false);
                  setIsOpen(true);
                }}
              >
                Edit recipients
              </button>
            )}
            {!!documentAvailable && <DownloadTranscriberButton item={item} completeButton={true} />}
            <button
              className={
                'text-red-dark list-action-button w-100 general-button text-left specific-y-padding-5 specific-x-padding-24 flex-centered gap-10'
              }
              onClick={() => {
                setShowActions(false);
                setDeleteItem({ ...item, finished });
                setShowDeleteModal(true);
              }}
            >
              {finished ? 'Delete' : 'Cancel'} transcript
            </button>
          </div>
        )}
      </div>
    </>
  );
};

const DownloadTranscriberButton = ({ item, completeButton }) => {
  const { id, eventTitle } = item ?? {};
  const [isLoading, setIsLoading] = useState(false);

  const { downloadTranscript: fetchTranscriptFile } = useTranscripts();
  const downloadTranscript = async () => {
    setIsLoading(true);
    try {
      const { blob, filename } = await fetchTranscriptFile({ id });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', downloadUrl);
      link.setAttribute('download', filename || normalize(eventTitle.trim()));
      document.body.appendChild(link); // Required for FF
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <button
      data-tooltip-id={'settings-tooltip'}
      data-tooltip-content={!isLoading && !completeButton ? 'Download transcript' : ''}
      data-tooltip-place='top'
      className={` ${completeButton ? 'list-action-button w-100 general-button text-left specific-y-padding-5 specific-x-padding-24' : 'ml-2 general-button hovered-button border rounded'}`}
      disabled={isLoading}
      onClick={(e) => {
        preventLink(e);
        downloadTranscript();
      }}
    >
      {completeButton ? (
        <span>
          Download transcript{isLoading && <i className='fas fa-spinner fa-spin d-inline-block-centered ml-2' />}
        </span>
      ) : (
        <>
          {isLoading ? (
            <i className='fas fa-spinner fa-spin d-inline-block-centered specific-x-margin-6' />
          ) : (
            <>
              <span className='icon-download-attachment paragraph-p4 specific-x-padding-6 ' />
            </>
          )}
        </>
      )}
    </button>
  );
};

export default TranscriptsItem;
